import GIS from "./app"
import {
    AllGeoJSON,
    booleanContains, booleanCrosses,
    booleanOverlap, booleanPointOnLine,
    booleanTouches,
    booleanWithin,
    buffer,
    difference,
    feature,
    featureCollection,
    flatten,
    intersect,
    lineIntersect, polygon,
    union,
    Units
} from "@turf/turf";
import GeoJSON from "ol/format/GeoJSON";
import {getIdProperty, highestIntegerId, CURRENT_STYLENAME_PROPERTY} from "./function/layer"
import {
    areaGeometryMagnitude,
    areaGeometryRings,
    DEFAULT_SLIVER_MAX_WIDTH,
    DEFAULT_SLIVER_MIN_AREA,
    DEFAULT_SNAP_TOLERANCE_RELATIVE,
    isSliver,
    MeasureRing,
    removeGeometrySpikes,
    sanitizeAreaGeometry,
    snapAreaGeometry
} from "./function/topology";
import {generateRandomText, getLineOffsetMeters} from "./function/analysis";

import WKT from "ol/format/WKT";
import {Feature} from "ol";
import {Circle, Geometry, Polygon as OlPolygon} from "ol/geom";
import {
    GetClosestFeatureOption,
    GetOverlapsOption,
    NeighborOfPolygonsOption,
    IncrementIdOption,
    OverlappingPair,
    SnapUnionOption,
    SpikeRemovalOption,
    TurfFeatureGeneric,
    UpdateOption
} from "./configuration/analysis";
import {SliverThresholds, SpikeThresholds} from "./configuration/topology";
import {
    CreateLineOffsetExportFormat,
    EPSGOptions,
    GetFeatureByIDOptions,
    GetIdPropertyOptions,
    GetIdsPropertyOptions,
    Relation,
    SpatialRelationOptions
} from "./general/options";
import {
    Distance,
    GetDifference,
    GetFeatureId,
    GetFeaturesAttributesContainedInFeature,
    GetFeaturesId,
    GetGeoJSONs,
    GetIntersect, GetFeaturesCheckedTypes,
    GetOverlappingPairs,
    GetUnion
} from "./general/callback";
import {Coordinate} from "ol/coordinate";
import {transform} from "./map";
import {FeatureCollection, MultiPolygon, Polygon} from "geojson";
import {fromCircle} from "ol/geom/Polygon";

interface BufferOptions {
    units?: Units;
    steps?: number;
}

/**
 * Represented as a JSON Feature (TurfJS)
 */
export type TurfFeatureType = GeoJSON | WKT | string | TurfFeatureGeneric;

type GeometryType = {
    type: string;
    coordinates: any[];
};

type FeatureType = {
    type: string;
    geometry: GeometryType;
};

class Analysis {
    private readonly GIS: GIS;

    private readonly GEOJSON_FORMAT: 'geojson' = 'geojson';
    private SIMPLE_FORMAT: string = 'simple';

    /**
     * Shared GeoJSON format instance, reused to avoid per-call allocations in hot paths.
     */
    private readonly geoJSONFormat = new GeoJSON();

    constructor(GIS: GIS) {
        this.GIS = GIS;
    }

    private checkJSON (variable: unknown) {
        if (typeof variable === 'string') {
            return JSON.parse(variable);
        } else {
            return variable;
        }
    }

    private isFeature(obj: FeatureType) {
        return obj && obj.type === "Feature" && obj.geometry && true && Array.isArray(obj.geometry.coordinates);
    }

    private getFeatureType(feature: any): string {
        const fJSON = this.checkJSON(feature);
        return fJSON.geometry?.type || fJSON.type;
    }

    /**
     * Normalizes a geometry/feature representation (string, geometry JSON or Feature JSON) into a Turf Feature.
     */
    private toTurfFeature (geometry: TurfFeatureType) {
        const json = this.checkJSON(geometry);
        return this.isFeature(json) ? json : feature(json);
    }

    private prepareCollection (geometry1: TurfFeatureType, geometry2: TurfFeatureType) {
        return featureCollection([this.toTurfFeature(geometry1), this.toTurfFeature(geometry2)]) as FeatureCollection<Polygon | MultiPolygon>;
    }

    /**
     * Subtracts many features from a base feature in a single polygon-clipping pass.
     * Much faster than sequential pairwise difference calls for many subtrahends.
     */
    private differenceMany (base: TurfFeatureType, subtrahends: TurfFeatureType[]) {
        if (subtrahends.length === 0) {
            return this.toTurfFeature(base);
        }

        const features = [this.toTurfFeature(base), ...subtrahends.map(subtrahend => this.toTurfFeature(subtrahend))];

        return difference(featureCollection(features) as FeatureCollection<Polygon | MultiPolygon>);
    }

    private lineIntersectFeatures (feature1: TurfFeatureType, feature2: TurfFeatureType) {
        return lineIntersect(this.checkJSON(feature1), this.checkJSON(feature2));
    }

    private bufferFeature (feature: TurfFeatureType, radius: number, options?: BufferOptions) {
        return buffer(this.checkJSON(feature), radius, options);
    }

    /**
     * Intersect two geometries.
     * @param geometry1 - Represented as a JSON Feature (TurfJS)
     * @param geometry2 - Represented as a JSON Feature (TurfJS)
     * @returns {Feature<Polygon | MultiPolygon>}
     */
    public differenceFeatures (geometry1: TurfFeatureType, geometry2: TurfFeatureType) {
        return difference(this.prepareCollection(geometry1, geometry2));
    }

    /**
     * Number of polygon parts of a Feature or geometry — `1` for a Polygon, the
     * member count for a MultiPolygon, `0` for anything else. What "did this union
     * actually come out in one piece" is measured by.
     */
    private areaPartCount (geoJson: any): number {
        const geometry = geoJson?.type === 'Feature' ? geoJson.geometry : geoJson;
        if (!geometry) return 0;
        if (geometry.type === 'Polygon') return 1;
        if (geometry.type === 'MultiPolygon') return geometry.coordinates.length;
        return 0;
    }

    /**
     * The gap-closing tolerance for a union, in map units.
     *
     * Derived from the coordinate magnitude of the two operands rather than fixed,
     * because the operands can be metres, feet or degrees depending on the layer's
     * projection and there is no one distance that is "a few millimetres" in all of
     * them — see {@link DEFAULT_SNAP_TOLERANCE_RELATIVE}. An explicit `snapTolerance`
     * is taken as given, in map units; `false` (or a non-positive number) turns the
     * pass off.
     */
    private unionSnapTolerance (feature1: any, feature2: any, snapTolerance?: number | false): number {
        if (snapTolerance === false) return 0;
        if (typeof snapTolerance === 'number') return snapTolerance;

        const magnitude = Math.max(
            areaGeometryMagnitude(feature1?.geometry),
            areaGeometryMagnitude(feature2?.geometry)
        );

        return magnitude * DEFAULT_SNAP_TOLERANCE_RELATIVE;
    }

    /**
     * Union of two area features, with a hairline gap between them closed first.
     *
     * A polygon clipper answers the question it is asked exactly: two polygons that
     * do not quite touch have a union in two pieces, and it returns a MultiPolygon.
     * That is the wrong answer for the parts of a split polygon. {@link splitPolygon}
     * cuts by subtracting the *buffered* cutting line, so its parts are left a
     * corridor of about 0.0002 m apart — sub-millimetre, invisible on any map, and
     * enough to make the parts genuinely disjoint. Re-uniting them therefore gave
     * back the two parts as a MultiPolygon rather than the polygon they were cut
     * from, and `booleanTouches` reported them as not touching, so with `update: true`
     * the layer was left alone as well.
     *
     * The gap is closed by SNAPPING rather than by buffering: `geometry2`'s boundary
     * is welded onto `geometry1`'s wherever the two run within `snapTolerance` of
     * each other ({@link snapAreaGeometry}), and the union is then re-run on the
     * welded pair. Snapping works directly in the layer's own projection, moves only
     * the vertices along the shared stretch and leaves the rest of both outlines
     * bit-for-bit intact — where buffering the split line would have to detour
     * through WGS84 (turf's `buffer` reads its input as lon/lat), and a
     * dilate-then-erode pass would re-emit every coordinate of the result and round
     * every corner it has.
     *
     * The pass is deliberately a FALLBACK, and a checked one:
     *
     * - it runs only when the plain union really did come back in more than one
     *   piece, so a union that was already correct is returned untouched,
     * - its result is kept only when it merged something. Two features that are
     *   genuinely apart still union into a MultiPolygon, which is the documented
     *   answer for them.
     *
     * @param geometry1 - The feature the boundary is welded ONTO; keeps its coordinates.
     * @param geometry2 - The feature whose boundary is welded; only its vertices move.
     * @param snapTolerance - {@link SnapUnionOption.snapTolerance}.
     */
    private unionFeaturesClosingGaps (geometry1: TurfFeatureType, geometry2: TurfFeatureType, snapTolerance?: number | false) {
        const feature1 = this.toTurfFeature(geometry1);
        const feature2 = this.toTurfFeature(geometry2);

        const plain = union(featureCollection([feature1, feature2]) as FeatureCollection<Polygon | MultiPolygon>);
        const plainParts = this.areaPartCount(plain);

        if (!plain || plainParts < 2) {
            return plain;
        }

        const tolerance = this.unionSnapTolerance(feature1, feature2, snapTolerance);
        if (!(tolerance > 0)) {
            return plain;
        }

        const {geometry, movedVertices, insertedVertices} = snapAreaGeometry(feature2.geometry, feature1.geometry, tolerance);
        if (movedVertices === 0 && insertedVertices === 0) {
            return plain;
        }

        const welded = union(featureCollection([feature1, {...feature2, geometry}]) as FeatureCollection<Polygon | MultiPolygon>);

        return welded && this.areaPartCount(welded) < plainParts ? welded : plain;
    }

    /**
     * Planar area of a Polygon / MultiPolygon in SQUARE MAP UNITS.
     *
     * Deliberately planar (shoelace) instead of turf's `area`, which interprets
     * coordinates as WGS84 lon/lat. Geometries here are in the view projection
     * (metres for EPSG:3857), so turf would report nonsense for them.
     */
    private planarArea (geoJson: any): number {
        const geometry = geoJson?.type === 'Feature' ? geoJson.geometry : geoJson;
        if (!geometry) return 0;

        const ringArea = (ring: number[][]): number => {
            let sum = 0;
            for (let index = 0, last = ring.length - 1; index < last; index++) {
                sum += ring[index][0] * ring[index + 1][1] - ring[index + 1][0] * ring[index][1];
            }
            return Math.abs(sum) / 2;
        };
        // First ring is the outline, the rest are holes.
        const ringsArea = (rings: number[][][]): number =>
            rings.reduce((total, ring, index) => index === 0 ? total + ringArea(ring) : total - ringArea(ring), 0);

        if (geometry.type === 'Polygon') return ringsArea(geometry.coordinates);
        if (geometry.type === 'MultiPolygon') {
            return (geometry.coordinates as number[][][][])
                .reduce((total, rings) => total + ringsArea(rings), 0);
        }
        return 0;
    }

    /**
     * Area of `geometry1` lying OUTSIDE `geometry2`, in square map units.
     *
     * The measure behind the `'coveredBy'` relation: a shared vertex or a shared
     * stretch of border contributes ZERO area, so only real encroachment
     * registers. Exposed because it answers "by HOW MUCH does this leave the
     * parcel", which a boolean relation cannot.
     *
     * @example
     * const compartment = GIS.Layer.getFeatureByID('forest_compartment', 14776147, { format: 'geojson' });
     * const parcel = GIS.Layer.getFeatureByID('real_estates', '10953400020001', { attribute: 'TUNNUS', format: 'geojson' });
     * GIS.Analysis.areaOutside(compartment, parcel); // 0 when fully inside
     */
    public areaOutside (geometry1: TurfFeatureType, geometry2: TurfFeatureType): number {
        const leftover = this.differenceFeatures(this.checkJSON(geometry1), this.checkJSON(geometry2));
        if (!leftover) return 0;
        return this.planarArea(leftover);
    }

    /** Default `'coveredBy'` tolerance in square map units. */
    private static readonly COVERED_BY_TOLERANCE = 1e-6;

    /**
     * True when `feature1` is covered by `feature2` — inside, boundary contact
     * allowed. Drives the `'coveredBy'` relation; see {@link Relation}.
     */
    private checkCoveredBy (
        feature1: TurfFeatureType,
        feature2: TurfFeatureType,
        tolerance: number = Analysis.COVERED_BY_TOLERANCE
    ): boolean {
        return this.areaOutside(feature1, feature2) <= tolerance;
    }

    public booleanPointOnLineCheck (point: TurfFeatureType, line: TurfFeatureType): boolean {
        return booleanPointOnLine(this.checkJSON(point), this.checkJSON(line));
    }

    /**
     * Intersect two geometries.
     * @param geometry1 - Represented as a JSON Feature (TurfJS)
     * @param geometry2 - Represented as a JSON Feature (TurfJS)
     */
    public intersectFeatures (geometry1: TurfFeatureType, geometry2: TurfFeatureType) {
        return intersect(this.prepareCollection(geometry1, geometry2));
    }

    /**
     * Split a MultiPolygon into one single-Polygon Feature per part. Returns
     * `null` for any other geometry type.
     *
     * Turf's `booleanContains` / `booleanWithin` throw
     * "feature1 MultiPolygon geometry not supported" — they simply do not accept
     * a MultiPolygon in either position. Real-world data hits this constantly
     * (a real-estate holding is routinely several detached parcels), so the
     * predicates below decompose and combine the parts themselves.
     */
    private multiPolygonParts (geoJson: any): any[] | null {
        const geometry = geoJson?.type === 'Feature' ? geoJson.geometry : geoJson;
        if (!geometry || geometry.type !== 'MultiPolygon' || !Array.isArray(geometry.coordinates)) return null;
        return geometry.coordinates.map((coordinates: unknown) => ({
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates }
        }));
    }

    /**
     * Check if one feature contains another feature.
     *
     * MultiPolygon-aware (see {@link multiPolygonParts}): a MultiPolygon
     * contains a geometry when ONE of its parts does — the parts are detached,
     * so nothing can straddle them — and a MultiPolygon is contained when EVERY
     * one of its parts is.
     *
     * @param geometry1 - Represented as a JSON Feature (TurfJS)
     * @param geometry2 - Represented as a JSON Feature (TurfJS)
     * @returns {boolean}
     */
    public booleanContainsFeatures (geometry1: TurfFeatureType, geometry2: TurfFeatureType): boolean {
        const container = this.checkJSON(geometry1);
        const contained = this.checkJSON(geometry2);

        const containedParts = this.multiPolygonParts(contained);
        if (containedParts) {
            return containedParts.length > 0 &&
                containedParts.every(part => this.booleanContainsFeatures(container, part));
        }

        const containerParts = this.multiPolygonParts(container);
        if (containerParts) {
            return containerParts.some(part => this.booleanContainsFeatures(part, contained));
        }

        return booleanContains(container, contained);
    }

    /**
     * Check if one feature lies within another.
     *
     * MultiPolygon-aware: a MultiPolygon is within another geometry only when
     * EVERY one of its parts is. Turf already handles a MultiPolygon in the
     * SECOND position, so only the first needs decomposing.
     */
    public booleanWithin (geometry1: TurfFeatureType, geometry2: TurfFeatureType): boolean {
        const inner = this.checkJSON(geometry1);
        const outer = this.checkJSON(geometry2);

        const innerParts = this.multiPolygonParts(inner);
        if (innerParts) {
            return innerParts.length > 0 && innerParts.every(part => this.booleanWithin(part, outer));
        }

        return booleanWithin(inner, outer);
    }

    public booleanCrossesFeatures = (geometry1: TurfFeatureType, geometry2: TurfFeatureType) => {
        return booleanCrosses(this.checkJSON(geometry1), this.checkJSON(geometry2));
    }

    private booleanTouchesFeatures (geometry1: TurfFeatureType, geometry2: TurfFeatureType) {
        return booleanTouches(this.checkJSON(geometry1), this.checkJSON(geometry2));
    }

    /**
     * Bounding box of an area geometry as `[minX, minY, maxX, maxY]`, or `null` when
     * it has no coordinates. Only used to reject pairs that cannot possibly be
     * adjacent before the far more expensive vertex-by-vertex snapping runs.
     */
    private areaBoundingBox (geometry: any): [number, number, number, number] | null {
        let box: [number, number, number, number] | null = null;

        for (const ring of areaGeometryRings(geometry)) {
            for (const vertex of ring) {
                if (!box) {
                    box = [vertex[0], vertex[1], vertex[0], vertex[1]];
                    continue;
                }
                box[0] = Math.min(box[0], vertex[0]);
                box[1] = Math.min(box[1], vertex[1]);
                box[2] = Math.max(box[2], vertex[0]);
                box[3] = Math.max(box[3], vertex[1]);
            }
        }

        return box;
    }

    /**
     * Widens an extent by the hairline-gap tolerance, so an extent query returns the
     * features {@link touchesFeatures} would still call adjacent. Derived from the
     * extent's own coordinate magnitude, for the reason given in
     * {@link DEFAULT_SNAP_TOLERANCE_RELATIVE}: a fixed distance means millimetres in
     * a metre projection and hundreds of kilometres in degrees.
     */
    private padExtent (extent: number[]): number[] {
        if (!Array.isArray(extent) || extent.length !== 4 || extent.some((value) => !isFinite(value))) {
            return extent;
        }

        const tolerance = Math.max(...extent.map(Math.abs)) * DEFAULT_SNAP_TOLERANCE_RELATIVE;

        return [extent[0] - tolerance, extent[1] - tolerance, extent[2] + tolerance, extent[3] + tolerance];
    }

    /**
     * True when two area features touch, INCLUDING across a hairline gap.
     *
     * `booleanTouches` answers the question exactly: two polygons a fraction of a
     * millimetre apart do not touch, and it says so. That is the wrong answer for
     * the parts of a split polygon — {@link splitPolygon} cuts by subtracting the
     * *buffered* cutting line, so its parts are left a corridor of about 0.0002 m
     * apart and are, strictly, disjoint. A neighbour query run right after a split
     * therefore reported the parts as unrelated to each other and to everything the
     * cut ran along, which is why `getSpatialRelatedFeaturesIds` came back empty.
     *
     * The gap is closed the same way {@link unionFeaturesClosingGaps} closes it for
     * a union: `geometry2`'s boundary is welded onto `geometry1`'s wherever the two
     * run within the snap tolerance of each other ({@link snapAreaGeometry}), and
     * the touch test is re-run on the welded pair. The tolerance is relative to the
     * coordinate magnitude ({@link DEFAULT_SNAP_TOLERANCE_RELATIVE}), which lands on
     * a few millimetres in every supported projection — far below any gap that means
     * something, so two features that are genuinely apart still do not touch.
     *
     * The weld is a FALLBACK: it runs only when the plain test already said no, only
     * for area geometries, and only for a pair whose bounding boxes come within the
     * tolerance of each other.
     */
    private touchesFeatures = (geometry1: TurfFeatureType, geometry2: TurfFeatureType): boolean => {
        if (this.booleanTouchesFeatures(geometry1, geometry2)) {
            return true;
        }

        const feature1 = this.toTurfFeature(geometry1);
        const feature2 = this.toTurfFeature(geometry2);

        const isArea = (candidate: any) =>
            candidate?.geometry?.type === 'Polygon' || candidate?.geometry?.type === 'MultiPolygon';

        if (!isArea(feature1) || !isArea(feature2)) {
            return false;
        }

        const tolerance = this.unionSnapTolerance(feature1, feature2);
        if (!(tolerance > 0)) {
            return false;
        }

        const box1 = this.areaBoundingBox(feature1.geometry);
        const box2 = this.areaBoundingBox(feature2.geometry);
        if (!box1 || !box2 ||
            box1[0] - tolerance > box2[2] || box2[0] - tolerance > box1[2] ||
            box1[1] - tolerance > box2[3] || box2[1] - tolerance > box1[3]) {
            return false;
        }

        const {geometry, movedVertices, insertedVertices} = snapAreaGeometry(feature2.geometry, feature1.geometry, tolerance);
        if (movedVertices === 0 && insertedVertices === 0) {
            return false;
        }

        return booleanTouches(feature1, {...feature2, geometry});
    }

    private booleanOverlapFeatures = (geometry1: TurfFeatureType, geometry2: TurfFeatureType) => {
        return booleanOverlap(this.checkJSON(geometry1), this.checkJSON(geometry2));
    }

    private checkTheSameType (feature1: TurfFeatureType, feature2: TurfFeatureType) {
        const type1 = this.checkJSON(feature1).type;
        const type2 = this.checkJSON(feature2).type;
        return !((type1 === "MultiPoint" && type2 !== "MultiPoint") ||
            ((type1 === "LineString" || type1 === "MultiLineString") &&
                type2 !== "LineString" &&
                type2 !== "MultiLineString") ||
            ((type1 === "Polygon" || type1 === "MultiPolygon") &&
                type2 !== "Polygon" &&
                type2 !== "MultiPolygon"));
    }

    /**
     * True when two (layer, id) pairs name one and the same feature — what the
     * neighbour queries use to keep the queried feature out of its own result.
     *
     * Ids are compared as STRINGS, exactly as {@link Layer.getOlFeatureByAttributeID}
     * matches them when it looks a feature up. A strict `===` here was type-sensitive
     * while the lookup was not, so a feature whose id property held the string `'2'`
     * and was queried as the number `2` failed the check and was reported as related
     * to itself — the only result the query returned, since a feature always contains
     * itself. Ids arrive from application code (`setFeatureAttributeByID` writes
     * whatever it is given) and from the map's own data, so the two forms genuinely
     * do mix.
     */
    private isTheSameFeature (layerName1: string, id1: string | number | undefined, layerName2: string, id2: string | number | undefined) {
        return layerName1 === layerName2 && String(id1) === String(id2);
    }

    private isTheSameType (feature1: TurfFeatureType, feature2: TurfFeatureType, callback?: GetFeaturesCheckedTypes) {
        if (this.checkTheSameType(feature1, feature2)) {
            return callback ? callback(feature1, feature2) : (false as const);
        } else {
            return false;
        }
    }

    /**
     * True when a whole feature is a {@link isSliver sliver} — no meaningful area, or
     * a needle. Measured geodesically over the complete geometry (holes subtracted
     * from the area, every ring counted in the perimeter), so the verdict is a
     * statement about the shape on the ground.
     *
     * A feature without geometry counts as a sliver: there is nothing to keep.
     */
    private isSliverFeature(geoJSONFeature: TurfFeatureGeneric | AllGeoJSON, thresholds?: SliverThresholds): boolean {
        const olFeature = this.geoJSONFormat.readFeature(geoJSONFeature as any) as Feature;
        const geometry = olFeature?.getGeometry();

        if (!geometry) {
            return true;
        }

        return isSliver(
            this.GIS.Projection.measure(geometry, 'area'),
            this.GIS.Projection.measure(geometry, 'perimeter'),
            thresholds
        );
    }

    /**
     * The attribute set of a source feature, ready to be copied onto the result of a
     * geometric operation. `getFeatureAttributesByID` hands back everything the live
     * OpenLayers feature carries, which includes two things that are not data:
     * the `geometry` itself and the internal
     * {@link CURRENT_STYLENAME_PROPERTY} bookkeeping flag. Copying the style flag onto
     * a NEW feature (as {@link splitPolygon} and {@link multiPolygonToPolygons} do)
     * would make that feature claim a style it does not actually render with — e.g. the
     * `_select` style of the source feature that happened to be selected when the
     * operation ran.
     * @param attributes - Raw attributes as returned by `getFeatureAttributesByID`.
     */
    private copyableAttributes(attributes: {[p: string]: any} | undefined | null): {[p: string]: any} {
        const copy = {...(attributes ?? {})};
        delete copy.geometry;
        delete copy[CURRENT_STYLENAME_PROPERTY];
        return copy;
    }

    /**
     * Rewrites the measurement properties of an operation result from the result's own
     * geometry, in place.
     *
     * Every geometric operation copies the source feature's attributes onto its result.
     * For a *measured* feature (one drawn/edited with the area & border-length overlay,
     * so it carries `cfm_*` properties) those copied values describe the shape BEFORE
     * the operation, so the result would report the wrong area — and each part of a
     * split would report the area of the whole pre-split polygon. Recomputing from the
     * result's own geometry keeps the reported values and the rendered `area` label
     * consistent with the shape actually returned.
     *
     * Measurement properties of a kind the result does not have are removed rather than
     * left behind: they describe the shape the result was cut from and there is no value
     * to replace them with — see {@link Edit.obsoleteMeasurementProperties}.
     *
     * A feature that never carried measurement properties is left untouched, so
     * measurements are never injected into features that did not display them — the same
     * rule {@link Edit.recalculateMeasurement} follows.
     *
     * @param properties - The result's properties, as copied from the source feature; mutated in place.
     * @param resultGeoJSON - The result feature, with coordinates in the map view projection.
     */
    private refreshResultMeasurement(properties: {[p: string]: any} | undefined | null, resultGeoJSON: any): void {
        if (!properties || !this.GIS.Edit.hasMeasurementValues(properties)) return;

        const olFeature = this.geoJSONFormat.readFeature(resultGeoJSON) as Feature;
        const geometry = olFeature?.getGeometry();
        if (!geometry) return;

        const measurement = this.GIS.Edit.computeMeasurementProperties(geometry);
        if (!measurement) return;

        for (const property of this.GIS.Edit.obsoleteMeasurementProperties(geometry, properties)) {
            delete properties[property];
        }

        Object.assign(properties, measurement);
    }

    /**
     * Real-world measurement of one linear ring — square meters and meters — the
     * {@link MeasureRing} the pure sliver tests need.
     *
     * Geodesic on purpose: the sliver decision is a statement about a width on the
     * ground, so measuring in raw projection units would make it mean something
     * different in every projection (an EPSG:3857 "metre" is ~2x too long at 60°N).
     */
    private readonly measureRing: MeasureRing = (ring: number[][]) => {
        const geometry = new OlPolygon([ring]);
        return {
            area: this.GIS.Projection.measure(geometry, 'area'),
            perimeter: this.GIS.Projection.measure(geometry, 'perimeter')
        };
    };

    /**
     * Cleans the raw output of a polygon-clipping operation (`intersect`,
     * `difference`, `union`) into geometry that is safe to render, measure and
     * persist.
     *
     * **Why every clip result has to go through here.** The clipper works in exact
     * arithmetic only in theory; in practice, whenever two input boundaries are
     * *nearly* coincident it has to decide which side of the other edge each vertex
     * falls on, and that decision comes down to the last bits of floating-point
     * precision. Nearly-coincident boundaries are not an edge case for drawn
     * purchase areas — they are the normal case, because the user snaps a new
     * polygon onto an existing one, so the two edges are the same line to within
     * snapping precision. The clipper then returns, along the whole shared
     * boundary:
     *
     * - hair-thin **sliver parts** — a strip 400 m long and a centimetre wide. Its
     *   area (a dozen m²) sails past any absolute area filter, so it survives as a
     *   visible "splinter" and, worse, as an extra *area* in the result: a
     *   `difference` that should have come back empty (the new polygon fully covers
     *   the old one) instead reports a second polygon and inflates the hectare total.
     * - **spikes** — needle protrusions on the genuine parts, where the ring runs
     *   out along the shared edge and doubles straight back.
     * - **degenerate rings** and duplicate vertices, which make the result fail
     *   validity checks even when its area happens to look right.
     *
     * All of them are removed by {@link sanitizeAreaGeometry}. Surviving parts are
     * merged back into one Feature; because merging two parts that touch can itself
     * produce a needle at the join, the merged result is sanitized once more —
     * without the sliver filter, since two genuine parts cannot union into a sliver
     * and the merged shape must never be thrown away.
     *
     * @param geojson - Raw Feature or Geometry as returned by the clipper.
     * @param thresholds - Optional {@link SliverThresholds} overriding the defaults.
     * @returns The cleaned Feature, or `null` when nothing of substance survived —
     * which is the correct answer for an operation whose true result is empty.
     */
    private cleanClipResult(geojson: AllGeoJSON, thresholds?: SliverThresholds): TurfFeatureGeneric | null {
        if (!geojson) return null;

        const flattened = flatten(geojson);

        const cleanedFeatures = flattened.features
            .map((part) => this.sanitizeFeature(part as unknown as TurfFeatureGeneric, thresholds))
            .filter((part): part is TurfFeatureGeneric => part !== null);

        if (cleanedFeatures.length === 0) return null;
        if (cleanedFeatures.length === 1) return cleanedFeatures[0];

        const collection = featureCollection(cleanedFeatures as any) as FeatureCollection<Polygon | MultiPolygon>;
        const combined = union(collection);

        if (!combined) return null;

        return this.sanitizeFeature(combined as unknown as TurfFeatureGeneric, false);
    }

    /**
     * Runs {@link sanitizeAreaGeometry} over one Feature, returning a new Feature
     * with the cleaned geometry, or `null` when the whole feature was an artifact.
     *
     * @param geoJSONFeature - Feature to clean; never mutated.
     * @param thresholds - {@link SliverThresholds} overrides, or `false` to clean
     * spikes and degenerate rings only and keep every part whatever its shape.
     */
    private sanitizeFeature(geoJSONFeature: TurfFeatureGeneric, thresholds?: SliverThresholds | false): TurfFeatureGeneric | null {
        if (!geoJSONFeature || !geoJSONFeature.geometry) return null;

        const {geometry} = sanitizeAreaGeometry(
            geoJSONFeature.geometry as any,
            this.measureRing,
            {slivers: thresholds === false ? false : (thresholds ?? {})}
        );

        return geometry ? {...geoJSONFeature, geometry} as TurfFeatureGeneric : null;
    }

    private decomposeMultiPolygon (multiPolygonFeature: MultiPolygon) {
        return multiPolygonFeature.coordinates.map(coords => {
            return polygon(coords);
        });
    };

    private checkCrosses (feature1: TurfFeatureType, feature2: TurfFeatureType) {
        const feature1JSON = this.checkJSON(feature1);
        const feature2JSON = this.checkJSON(feature2);

        const type1 = feature1JSON.geometry?.type || feature1JSON.type;
        const type2 = feature2JSON.geometry?.type || feature2JSON.type;

        if (type1 === 'Point' || type2 === 'Point') {
            return false;
        }

        const polygons1 = feature1JSON.type === 'MultiPolygon' ? this.decomposeMultiPolygon(feature1JSON) : [feature1JSON];
        const polygons2 = feature2JSON.type === 'MultiPolygon' ? this.decomposeMultiPolygon(feature2JSON) : [feature2JSON];

        for (let poly1 of polygons1) {
            for (let poly2 of polygons2) {
                const geometry1 = poly1.type === 'Feature' ? poly1.geometry : poly1;
                const geometry2 = poly2.type === 'Feature' ? poly2.geometry : poly2;

                // Wrap in try-catch just in case other unsupported combos appear
                try {
                    if (!(geometry1.type === 'Polygon' && geometry2.type === 'Polygon') && booleanCrosses(geometry1, geometry2)) {
                        return true;
                    }
                } catch (e) {
                    // Ignore unsupported geometry errors for crosses
                    return false;
                }
            }
        }
        return false;
    };

    private checkContains (feature1: TurfFeatureType, feature2: TurfFeatureType) {
        const feature1JSON = this.checkJSON(feature1);
        const feature2JSON = this.checkJSON(feature2);
        const polygons1 = feature1JSON.type === 'MultiPolygon' ? this.decomposeMultiPolygon(feature1JSON) : [feature1JSON];
        const polygons2 = feature2JSON.type === 'MultiPolygon' ? this.decomposeMultiPolygon(feature2JSON) : [feature2JSON];

        for (let poly1 of polygons1) {
            for (let poly2 of polygons2) {
                if (this.booleanContainsFeatures(poly1, poly2) || booleanWithin(poly2, poly1)) {
                    return true;
                }
            }
        }
        return false;
    };

    private checkRelation (relation: Relation, feature1: TurfFeatureType, feature2: TurfFeatureType, tolerance?: number) {
        // 1. Get types clearly
        const type1 = this.getFeatureType(feature1);
        const type2 = this.getFeatureType(feature2);

        const isSourcePoint = type1 === 'Point';
        const isSourceLine = type1 === 'LineString' || type1 === 'MultiLineString';

        const isTargetPoint = type2 === 'Point';
        const isTargetLine = type2 === 'LineString' || type2 === 'MultiLineString';
        const isTargetPolygon = type2 === 'Polygon' || type2 === 'MultiPolygon';

        const relationCheckMap: Record<'overlap' | 'contains' | 'crosses' | 'touch' | 'within', () => boolean> = {
            'overlap': () => this.isTheSameType(feature1, feature2, this.booleanOverlapFeatures),
            'contains': () => {
                return this.checkContains(feature1, feature2);
            },
            'crosses': () => this.checkCrosses(feature1, feature2),
            // `'touch'` is part of {@link Relation} and of what `'any'` means, but had
            // no entry here, so a query asking for it silently matched nothing — and
            // two features sharing only a border were never "any relation" either.
            // Adjacency across the hairline gap a split leaves behind counts; see
            // {@link touchesFeatures}. Gated on the operands being the same KIND of
            // geometry, exactly as `'overlap'` is, so the `'any'` sweep never hands
            // turf a combination it does not support.
            'touch': () => this.isTheSameType(feature1, feature2, this.touchesFeatures),
            'within': () => {
                if (isTargetLine) {
                    if (isSourcePoint) {
                        return this.booleanPointOnLineCheck(feature1, feature2);
                    }
                    return false;
                }

                if (isTargetPoint) return false;

                if (isSourceLine && isTargetPolygon) {
                    return this.booleanWithin(feature1, feature2);
                }

                return this.booleanWithin(feature1, feature2);
            }
        };

        if (relation.includes('any')) {
            if (isTargetLine && this.lineIntersectFeatures(feature1, feature2).features.length > 0) {
                return true;
            }

            for (const key of Object.keys(relationCheckMap) as Array<keyof typeof relationCheckMap>) {
                if (relationCheckMap[key]()) {
                    return true;
                }
            }
        }

        for (const key of Object.keys(relationCheckMap) as Array<keyof typeof relationCheckMap>) {
            if (relation.includes(key) && relationCheckMap[key]()) {
                return true;
            }
        }

        // Opt-in only — deliberately outside the 'any' sweep above, so existing
        // `relation: ['any']` results are unchanged by its addition.
        if (relation.includes('coveredBy') && this.checkCoveredBy(feature1, feature2, tolerance)) {
            return true;
        }

        return false;
    };

    /**
     * Get spatial related ids one to many features from the first layer with one feature from the second layer specified by ID
     * @param layerName1 - Layer name of a first layer
     * @param ids1 - ID values in array or 'every'. It specifies either particular ids for difference comparison or every feature in the layer.
     * @param layerName2 - Layer name of a second layer.
     * @param id2 - ID value for the feature of the Second layer.
     * @param options - Default relation is "any", it means any spatial relation is possible. You can specify "overlap" or "contains" or both.
     * @param callback - Callback with IDs array.
     *
     * When both layer names are the same the queried feature is excluded from the
     * result — it is trivially related to itself. The comparison is made on the
     * STRING form of the ids, the same way {@link Layer.getFeatureByID} looks a
     * feature up, so it holds whatever the id property happens to hold: a feature
     * whose id was written as the string `'2'` and looked up as the number `2` is
     * still the same feature, and must not be reported as its own neighbour.
     */
    public getSpatialRelatedFeaturesIds(layerName1: string, ids1: (string | number)[] | string, layerName2: string, id2: string | number, options?: GetIdsPropertyOptions & SpatialRelationOptions, callback?: GetFeaturesId): (string | number)[] {
        const formatGeoJSON = new GeoJSON();

        const attribute1 = 'attribute1';
        const attribute2 = 'attribute2';
        let relation: Relation = ["any"];

        let idAttribute1 = getIdProperty(options, attribute1);
        let idAttribute2 = getIdProperty(options, attribute2);

        let feature1Options = {
            attribute: idAttribute1,
            format: this.GEOJSON_FORMAT
        }

        let feature2Options = {
            attribute: idAttribute2,
            format: this.GEOJSON_FORMAT
        }

        if (options && options.relation) {
            relation = options.relation as Relation;
        }

        const coveredByTolerance = options?.tolerance;

        let feature1;
        let feature2 = this.GIS.Layer.getFeatureByID(layerName2, id2, feature2Options as GetFeatureByIDOptions);

        let booleanRelated: (string | number)[]  = [];

        if (typeof ids1 !== 'string' && ids1.length > 0) {
            ids1.map(id1 => {
                if (this.isTheSameFeature(layerName1, id1, layerName2, id2)) {
                    return;
                }

                feature1 = this.GIS.Layer.getFeatureByID(layerName1, id1, feature1Options  as GetFeatureByIDOptions);
                const related = this.checkRelation(relation,feature1, feature2, coveredByTolerance);
                if (related) {
                    booleanRelated.push(id1);
                }
            });
        } else if (typeof ids1 === 'string' && ids1.toLowerCase() === 'every') {
            const layer = this.GIS.Layer.getLayer(layerName1);

            if (layer.getSource().getFeatures().length < 2 && layerName1 === layerName2) {
                return [];
            } else if (layer.getSource().getFeatures().length < 1) {
                return [];
            }

            layer.getSource().forEachFeature((feature:Feature) => {

                const id = feature.get(idAttribute1);

                if (this.isTheSameFeature(layerName1, id, layerName2, id2)) {
                    return;
                }

                const geometry: Geometry | undefined = feature.getGeometry();
                if (!geometry) {
                    return;
                }

                const geometryRepresentation = formatGeoJSON.writeGeometry(geometry);

                const overlapped = this.checkRelation(relation,geometryRepresentation, feature2, coveredByTolerance);

                if (overlapped) {
                    booleanRelated.push(id);
                }
            });
        }

        if (callback) {
            callback(booleanRelated);
        }

        return booleanRelated;
    }

    /**
     * Find the closest feature specified by layerName1 and id from layerName2 features.
     * @param layerName1 Layer name of a first layer
     * @param id ID value for the feature of the First layer.
     * @param layerName2 Layer name of a second layer - searches the closest feature from this layer.
     * @param options - Filter and id attribute specification for both layers.
     * @param callback - Callback with ID of the closest feature.
     */
    public getClosestFeatureId = (layerName1: string, id: number | string, layerName2: string, options?: GetClosestFeatureOption & GetIdsPropertyOptions, callback?: GetFeatureId) => {
        const attribute1 = 'attribute1';
        const attribute2 = 'attribute2';

        let idAttribute1 = getIdProperty(options, attribute1);
        let idAttribute2 = getIdProperty(options, attribute2);

        const center = this.GIS.Layer.getFeatureCenterByID(layerName1, id, {
            attribute: idAttribute1
        });

        let filter;
        filter = (feature:Feature) => {
            if (options && options.filter && options.filter.equal) {
                return options.filter.equal.filter(id => String(feature.get(idAttribute2)) === String(id)).length > 0;
            } else if (options && options.filter && options.filter.notequal) {
                return !(options.filter.notequal.filter(id => String(feature.get(idAttribute2)) === String(id)).length > 0);
            } else if (layerName1 === layerName2) {
                return (String(feature.get(idAttribute2)) !== String(id));
            } else {
                return true;
            }
        }

        const feature = this.GIS.Layer.getLayer(layerName2).getSource().getClosestFeatureToCoordinate([center[0].x,center[0].y], filter);
        let closestFeatureId = null;
        if (feature) {
            closestFeatureId = feature.get(idAttribute2);
        }

        if (callback) {
            callback(closestFeatureId);
        }

        return closestFeatureId;
    }

    private getIncrement (layerName: string, idProperty: string) {
        const layer = this.GIS.Layer.getLayer(layerName);
        let uniqueId: string | number = 1;
        layer.getSource().forEachFeature((feature:Feature) => {
            let id = feature.get(idProperty)
            if (Number.isInteger(id)) {
                if (id >= uniqueId) {
                    uniqueId = id + 1;
                }
            } else {
                uniqueId = generateRandomText(10);
                return uniqueId;
            }
        })
        return uniqueId;
    }
    /**
     * Checks if features in the given layer with the specified IDs are neighbors of features in another layer.
     * The spatial relation is configurable through `options.relation` and may combine any of `'overlap'`,
     * `'touch'` and `'within'`:
     *
     * - `'overlap'` - features that partially overlap each other.
     * - `'touch'` - features that share a boundary.
     * - `'within'` - features that are fully contained inside, or that fully encapsulate, the queried
     *   feature. This detects compartments that are layered over each other (fully overlapping), which
     *   `'overlap'` and `'touch'` cannot find.
     *
     * If the same layer name is provided for both layerName1 and layerName2, the queried IDs are excluded
     * from the result.
     *
     * @param layerName1 - The name of the layer to check for neighboring features.
     * @param ids - The IDs of the features in layerName1 to check.
     * @param layerName2 - The name of the layer to check for neighboring features.
     * @param options - An object containing options for getting the feature IDs, format and the spatial relation(s).
     * @param callback - An optional callback function to call with the results.
     *
     * @returns An array of IDs of features in layerName2 that are neighbors of features in layerName1.
     *
     * @example
     * // Get all neighbor IDs of features with IDs [1, 2, 3] in layer "myLayer"
     * // that overlap or touch features in layer "otherLayer".
     * const ids = neighborOfPolygons("myLayer", [1, 2, 3], "otherLayer", {
     *   relation: ['overlap', 'touch']
     * }, (result) => {
     *   console.log(result);
     * });
     *
     * @example
     * // Get all compartments fully contained in (or fully containing) compartment 8.
     * const ids = neighborOfPolygons("myLayer", [8], "myLayer", { relation: ['within'] });
     */
    public neighborOfPolygons (layerName1: string, ids: (string | number)[], layerName2: string, options?: NeighborOfPolygonsOption & GetIdsPropertyOptions, callback?: GetFeaturesId) {
        const attribute1 = 'attribute1';
        const attribute2 = 'attribute2';

        let relation = ['overlap'];

        if (options && options.relation) {
            relation = options.relation;
        }

        let idAttribute = getIdProperty(options, attribute1);
        let returnIdAttribute = getIdProperty(options, attribute2);

        let feature1Options = {
            attribute: idAttribute,
            format: this.GEOJSON_FORMAT
        }

        const extent = this.GIS.Layer.getFeatureExtentByID(layerName1, ids, {attribute: idAttribute});

        // `getFeaturesInExtent` needs the extents to actually meet, so the candidate
        // set was missing exactly the neighbours {@link touchesFeatures} exists for:
        // the other part of a freshly split polygon sits a fraction of a millimetre
        // away, and its extent therefore fell just outside this one. Widening by the
        // same tolerance the touch test works at costs nothing — every candidate
        // still has to pass the relation check.
        const neighborhood = this.GIS.Layer.getLayer(layerName2).getSource()
            .getFeaturesInExtent(this.padExtent(extent));

        let featuresToCheck: (string | number)[] = [];

        ids.map(id => {
            const feature = this.GIS.Layer.getFeatureByID(layerName1, id, feature1Options as GetFeatureByIDOptions);

            if (feature) {
                neighborhood.map((neighbor:Feature<Geometry>) => {
                    const format = new GeoJSON();
                    const geometry = neighbor.getGeometry();
                    if (geometry) {
                        const neighborGeometry = format.writeGeometry(geometry, {
                            dataProjection: this.GIS.View.olObject.getProjection(),
                            featureProjection: this.GIS.View.olObject.getProjection()
                        });

                        const featureToPush = (relation.includes('touch') && this.touchesFeatures(feature, neighborGeometry)) ||
                            (relation.includes('overlap') && this.booleanOverlapFeatures(feature, neighborGeometry)) ||
                            (relation.includes('within') && (this.booleanWithin(neighborGeometry, feature) || this.booleanContainsFeatures(neighborGeometry, feature))) ||
                            // The neighbor COVERS the queried feature (boundary
                            // contact allowed) — the parcel a compartment must
                            // not leave. See {@link Relation}.
                            (relation.includes('coveredBy') && this.checkCoveredBy(feature, neighborGeometry, options?.tolerance))

                        if (featureToPush) {
                            featuresToCheck.push(neighbor.get(returnIdAttribute));
                        }
                    }
                })
            }
        })

        // Compared as strings, for the same reason as {@link isTheSameFeature}: the
        // queried ids come from application code while the ids being filtered were
        // read off the map's own features, so `includes` alone missed a queried
        // feature whose id property holds a number and was asked for as a string.
        featuresToCheck = featuresToCheck.filter(
            (candidate) => !ids.some((id) => this.isTheSameFeature(layerName1, id, layerName2, candidate))
        );

        if (callback) {
            callback(featuresToCheck);
        }

        return featuresToCheck;
    }

    /**
     * Calculates the difference and intersection between two polygons, given their layer names and IDs.
     * @param layerName1 - The name of the layer containing the polygon.
     * @param id1 - ID value for the feature of the polygon layer.
     * @param layerName2 - The name of the layer containing the linestring.
     * @param id2 - ID value for the feature of the linestring layer.
     * @param options - An optional options object that may contain the following properties:
     * @param callback - An optional function to call with a split polygon result.
     * @returns {Array} An array containing every polygon part produced by the cut. A single
     * cutting line may cross the polygon several times and therefore split it into more than
     * two parts — all of them are returned. The first part keeps the source feature's id and
     * attributes; every additional part receives a copy of the attributes with a fresh id.
     * Spikes left along the cut — needle-thin protrusions produced when the cutting line runs
     * along an existing polygon edge (e.g. a polygon drawn on the RE boundary) — are removed
     * from every part by default; see {@link SpikeRemovalOption.removeSpikes} to tune or disable.
     *
     * With `update: true` the fresh id is written under `attribute1`. Because every part is a
     * copy of the source attributes, a *second* id-like property would otherwise be duplicated
     * across the parts — e.g. cutting a layer keyed on `'OBJECTID'` leaves every part with the
     * same `'id'`, and since `getFeatureByID` returns the first match the later parts become
     * unreachable and the same polygon appears twice. That property is therefore renumbered as
     * well (default `'id'`); see {@link IncrementIdOption.incrementId} to point it at another
     * property or to switch it off. Only a value that is actually present is renumbered — a
     * part whose secondary id is `null` or missing is left untouched.
     */
    public splitPolygon (layerName1: string, id1: string | number, layerName2: string, id2: string | number, options?: GetIdsPropertyOptions & UpdateOption & SpikeRemovalOption & IncrementIdOption, callback?: GetGeoJSONs) {

        const BUFFER_RADIUS = 0.0001;
        const BUFFER_UNITS = 'meters';
        const BUFFER_EPSG = 'EPSG:4326';

        const attribute1 = 'attribute1';
        const attribute2 = 'attribute2';

        let idAttribute1 = getIdProperty(options, attribute1);
        let idAttribute2 = getIdProperty(options, attribute2);

        let feature1Options = {
            attribute: idAttribute1,
            format: this.GEOJSON_FORMAT
        }

        //TurfJS buffer operates only with WGS84
        //May case transformation shift!!! Within large areas.
        let feature2Options = {
            attribute: idAttribute2,
            format: this.GEOJSON_FORMAT,
            epsg: BUFFER_EPSG
        }

        let polygonFeature = this.GIS.Layer.getFeatureByID(layerName1, id1, feature1Options as GetFeatureByIDOptions, undefined);
        let lineFeature = this.GIS.Layer.getFeatureByID(layerName2, id2, feature2Options as GetFeatureByIDOptions, undefined);
        let lineFeatureTest = this.GIS.Layer.getFeatureByID(layerName2, id2, {attribute: feature2Options.attribute, format: feature2Options.format} as GetFeatureByIDOptions, undefined);

        if (this.lineIntersectFeatures(lineFeatureTest, polygonFeature).features.length < 2) {
            if (callback) {
                callback([]);
            }

            return [];
        }

        // The parts are NEW features, so the source's internal style flag must not ride
        // along with the copied attributes.
        const polygonAttributes = this.copyableAttributes(
            this.GIS.Layer.getFeatureAttributesByID(layerName1, id1, feature1Options)
        );

        //Result in WGS84
        const polygonCut = this.bufferFeature(lineFeature, BUFFER_RADIUS, {units: BUFFER_UNITS});

        const format = new GeoJSON();
        const polygonCutTransform = format.readFeature(polygonCut, {
            dataProjection: 'EPSG:4326',
            featureProjection: this.GIS.View.olObject.getProjection()
        }) as Feature<Geometry>;
        const polygonCutTransformGeometry = polygonCutTransform.getGeometry();
        if (polygonCutTransformGeometry) {
            const polygonCutGeometry = format.writeGeometry(polygonCutTransformGeometry, {
                dataProjection: this.GIS.View.olObject.getProjection(),
                featureProjection: this.GIS.View.olObject.getProjection()
            });

            // Subtracting the (buffered) cutting line from the polygon yields a MultiPolygon
            // whose members are the individual parts produced by the cut. `flatten` turns each
            // member into its own single-geometry Polygon feature, so a line that crosses the
            // polygon several times — and therefore splits it into more than two parts —
            // returns every part, not just two. Holes/inner rings stay on the part that owns
            // them. Slivers left where the buffer grazes the polygon boundary are discarded
            // so only the genuine parts are returned, each a single Polygon: both a
            // tiny-area and a needle-shaped artifact are rejected (see
            // {@link SliverThresholds}), the latter being the long thin strip that no
            // absolute area threshold can catch.
            const cut = this.differenceFeatures(polygonFeature, polygonCutGeometry) as AllGeoJSON;

            const flattenPolygons = flatten(cut);

            let parts: TurfFeatureGeneric[] = [];

            if (flattenPolygons && flattenPolygons.features.length > 1) {
                flattenPolygons.features.forEach((partFeature) => {
                    if (!this.isSliverFeature(partFeature as unknown as TurfFeatureGeneric)) {
                        parts.push(partFeature as unknown as TurfFeatureGeneric);
                    }
                });
            }

            // Cutting along an existing polygon edge (e.g. a polygon drawn on the RE boundary,
            // then split there) leaves needle-thin spikes on the resulting parts where the
            // buffered line grazes the boundary. Remove them from every part unless the caller
            // opted out via `removeSpikes: false`.
            if (options?.removeSpikes !== false) {
                const spikeThresholds: SpikeThresholds | undefined =
                    typeof options?.removeSpikes === 'object' ? options.removeSpikes : undefined;

                parts = parts.map((part) => ({
                    ...part,
                    geometry: removeGeometrySpikes(part.geometry, spikeThresholds).geometry
                }));
            }

            // The first part keeps the source feature's attributes and id; every additional
            // part receives a copy of the attributes. A measured source polygon carries the
            // area / border length of the WHOLE shape, so each part's measurement is
            // recomputed from its own geometry — otherwise every part would report the
            // pre-split area, both in the returned GeoJSON and on the layer.
            parts.forEach((part) => {
                part.properties = {...polygonAttributes};
                this.refreshResultMeasurement(part.properties, part);
            });

            if (parts.length > 1 && options && options.update) {
                const epsgCode = this.GIS.View.olObject.getProjection().getCode().replace('EPSG:','');

                // Every part is a copy of the source attributes, so any OTHER id-like
                // property (typically 'id', while the cut is keyed on e.g. 'OBJECTID')
                // would end up duplicated across the parts. Two features sharing an id
                // value make the second one unreachable — `getFeatureByID` returns the
                // first match — so the same polygon appears twice. Keep that property
                // unique as well, unless the caller opts out with `incrementId: false`.
                const secondaryIdProperty = options.incrementId === false
                    ? undefined
                    : typeof options.incrementId === 'string' ? options.incrementId : 'id';

                this.GIS.Layer.removeFeatureByID(layerName1, id1, feature1Options);

                parts.forEach((part, partIndex) => {
                    if (partIndex > 0 && part.properties) {
                        part.properties[idAttribute1] = this.getIncrement(layerName1, idAttribute1);

                        // Only a value that is actually there gets renumbered: a part whose
                        // secondary id is null/absent is left alone, so no id is invented
                        // for data that never had one.
                        if (secondaryIdProperty && secondaryIdProperty !== idAttribute1 &&
                            part.properties[secondaryIdProperty] !== undefined &&
                            part.properties[secondaryIdProperty] !== null) {
                            part.properties[secondaryIdProperty] = this.getIncrement(layerName1, secondaryIdProperty);
                        }
                    }
                    this.GIS.Layer.addFeatures(layerName1, part, {epsg: epsgCode});
                });
            }

            if (callback) {
                parts.length ? callback(parts) : callback(null);
            }

            return parts;
        }
    }
    /**
     * Distance in meters between two features.
     * @param layerName1 Layer name of a first layer
     * @param id1 ID value for the feature of the First layer.
     * @param layerName2 Layer name of a second layer
     * @param id2 value for the feature of the Second layer.
     * @param options
     * @param callback
     */
    public distanceBetweenFeatures (layerName1: string, id1: string | number, layerName2: string, id2: string | number, options?: GetIdsPropertyOptions, callback?: Distance) {
        const attribute1 = 'attribute1';
        const attribute2 = 'attribute2';

        let idAttribute1 = getIdProperty(options, attribute1);
        let idAttribute2 = getIdProperty(options, attribute2);

        let feature1Options = {
            attribute: idAttribute1,
            format: this.SIMPLE_FORMAT
        }

        let feature2Options = {
            attribute: idAttribute2,
            format: this.SIMPLE_FORMAT
        }

        let geometry1 = this.GIS.Layer.getFeatureByID(layerName1, id1, feature1Options as GetFeatureByIDOptions);
        let geometry1Coordinates = [];
        if (geometry1.getType() === 'Polygon') {
            geometry1Coordinates = geometry1.getLinearRing(0).getCoordinates();
        } else if (geometry1.getType() === 'Point') {
            geometry1Coordinates = [geometry1.getCoordinates()];
        } else if (geometry1.getType() === 'GeometryCollection') {
            for (let i = 0, max_i = geometry1.geometries_.length; i < max_i; i++) {
                geometry1Coordinates.push(geometry1.geometries_[i].getCoordinates());
            }
        } else {
            geometry1Coordinates = geometry1.getCoordinates();
        }

        let geometry2 = this.GIS.Layer.getFeatureByID(layerName2, id2, feature2Options as GetFeatureByIDOptions);
        let geometry2Coordinates = [];
        if (geometry2.getType() === 'Polygon') {
            geometry2Coordinates = geometry2.getLinearRing(0).getCoordinates();
        } else if (geometry2.getType() === 'Point') {
            geometry2Coordinates = [geometry2.getCoordinates()];
        } else if (geometry2.getType() === 'GeometryCollection') {
            for (let i = 0, max_i = geometry2.geometries_.length; i < max_i; i++) {
                geometry2Coordinates.push(geometry2.geometries_[i].getCoordinates());
            }
        } else {
            geometry2Coordinates = geometry2.getCoordinates();
        }

        let shortestLine: [Coordinate, Coordinate] | undefined;
        let distanceSq = Infinity;

        geometry1Coordinates.forEach((coordinates:Coordinate) => {
            const closest = geometry2.getClosestPoint(coordinates);
            const distanceNew = Math.pow(closest[0] - coordinates[0], 2) + Math.pow(closest[1] - coordinates[1], 2);
            if (distanceNew < distanceSq) {
                distanceSq = distanceNew;
                shortestLine = [coordinates, closest];
            }
        });

        geometry2Coordinates.forEach((coordinates:Coordinate) => {
            const closest = geometry1.getClosestPoint(coordinates);
            const distanceNew = Math.pow(closest[0] - coordinates[0], 2) + Math.pow(closest[1] - coordinates[1], 2);
            if (distanceNew < distanceSq) {
                distanceSq = distanceNew;
                shortestLine = [closest, coordinates];
            }
        });

        let distance = null;

        if (shortestLine) {
            const line = this.GIS.Layer.createGeometry('linestring', [
                shortestLine[0],
                shortestLine[1]
            ]);
            const epsgText = this.GIS.View.olObject.getProjection().getCode().replace('EPSG:','');
            distance = this.GIS.Projection.measure(line, 'length',epsgText)
        }

        if (callback) {
            distance ? callback(distance) : callback(null);
        }

        return distance;
    }
    /**
     * Union features of two layers based on their IDs.
     * If some features do not touch, overlap or contain, the map layer will not be updated.
     * If every feature meets the above spatial conditions and `update` is true, features in the first layer will be deleted except the union result.
     *
     * Features separated only by a hairline gap — most of all the parts of a polygon
     * that {@link splitPolygon} has just cut, which its buffered cutting line leaves
     * a fraction of a millimetre apart — are welded back together rather than
     * returned as a MultiPolygon; see
     * {@link unionFeaturesClosingGaps} for how, and
     * {@link SnapUnionOption.snapTolerance} for the tolerance and how to turn it off.
     * Features that are genuinely apart are unaffected and still union into a
     * MultiPolygon.
     *
     * @param layerName1 - The name of the layer to get one or more polygons from.
     * @param ids1 - The IDs of the features in layerName1 to use for union.
     * @param layerName2 - The name of the layer to union with, it is also layer which will be updated.
     * @param id2 - The ID of the feature in layerName2 to union with.
     * @param options - An object containing options for getting the feature IDs and format.
     * @param callback - An optional callback function to call with the results.
     */
    public unionFeaturesOfLayers (layerName1:string, ids1: (string | number) [], layerName2:string, id2: number | string, options?: GetIdsPropertyOptions & UpdateOption & EPSGOptions & SnapUnionOption, callback?: GetUnion) {
        const formatGeoJSON = new GeoJSON();

        let closeRelation = false;
        let update = false;
        let multipolygon = false;

        const attribute1 = 'attribute1';
        const attribute2 = 'attribute2';

        let idAttribute1 = getIdProperty(options, attribute1);
        let idAttribute2 = getIdProperty(options, attribute2);

        let feature1Options = {
            attribute: idAttribute1,
            format: this.GEOJSON_FORMAT
        }

        let feature2Options = {
            attribute: idAttribute2,
            format: this.GEOJSON_FORMAT
        }

        if (options && options.epsg) {
            feature1Options = {
                ...feature1Options
            }

            feature2Options = {
                ...feature2Options
            }
        }

        if (options) {
            if (options.update) {
                update = options.update;
            }

            if (options.multipolygon) {
                multipolygon = options.multipolygon;
            }
        }

        let union = this.GIS.Layer.getFeatureByID(layerName2, id2, feature2Options as GetFeatureByIDOptions);

        if (ids1.length > 0) {
            let feature1;
            ids1.map((id1: number | string) => {
                feature1 = this.GIS.Layer.getFeatureByID(layerName1, id1, feature1Options as GetFeatureByIDOptions);

                //Check if feature is in close relation to another one
                //If not it will not update only return multipolygon
                if (!multipolygon) {
                    closeRelation = this.booleanTouchesFeatures(union, feature1) ||
                        this.booleanOverlapFeatures(union, feature1) || this.booleanContainsFeatures(union, feature1) || this.booleanWithin(union, feature1)
                }

                const unionResult = this.unionFeaturesClosingGaps(union, feature1, options?.snapTolerance);

                // The split-gap case reaches here with every boolean relation false —
                // the parts really are disjoint, by a fraction of a millimetre — yet the
                // union came back as ONE polygon, which two features can only do when
                // they are adjacent. That is the relation the predicates missed, so the
                // layer is updated after all instead of being refused with a warning.
                if (!multipolygon && !closeRelation && this.areaPartCount(unionResult) === 1) {
                    closeRelation = true;
                }

                if (!closeRelation && update && !multipolygon) {
                    update = false;
                    console.warn('Cannot update layer, one of the features does not touch, overlap or contain. I will only return multipolygon.')
                }

                union = this.cleanClipResult(unionResult as AllGeoJSON);
            });
        } else {
            throw 'Empty or not valid first argument.'
        }

        if (union && update) {
            const format = new GeoJSON();
            const feature = format.readFeature(union, {
                dataProjection: this.GIS.View.olObject.getProjection(),
                featureProjection: this.GIS.View.olObject.getProjection()
            }) as Feature<Geometry>;

            const geometry = feature.getGeometry();

            const featureToUpdate = this.GIS.Layer.getOlFeatureByAttributeID(layerName2, idAttribute2, id2)
            featureToUpdate.setGeometry(geometry);

            //Updated union won't remove
            const index = ids1.indexOf(id2);
            if (index > -1) {
                ids1.splice(index, 1);
            }
        }

        if (union) {
            const attributes = this.GIS.Layer.getFeatureAttributesByID(layerName2, id2, feature2Options);
            delete attributes?.geometry;
            union.properties = {...attributes};

            // Refresh while the result is still in the view projection — the union covers
            // a larger shape than the source feature it inherited its attributes from.
            this.refreshResultMeasurement(union.properties, union);

            if (options && options.epsg) {
                const feature = formatGeoJSON.readFeature(union, {
                    dataProjection: this.GIS.View.olObject.getProjection(),
                    featureProjection: this.GIS.Projection.getRegistered(options.epsg)
                });

                union = JSON.parse(formatGeoJSON.writeFeature(feature as Feature<Geometry>));
            }

            if (layerName1 === layerName2 && update) {
                ids1.map(id1 => {
                    this.GIS.Layer.removeFeatureByID(layerName1, id1, {attribute: idAttribute1})
                })
            }
        }

        if (callback) {
            callback(union);
        }

        return union;
    }
    /**
     * Difference one to many features from the first layer with one feature from the second layer specified by IDs
     * Note: when you would like to create a hole inside another polygon. The second layer should be the outer ring and the first layer should be the inner ring.
     * @param layerName1 name of a first layer
     * @param ids1 The literal `'every'` or an array of id values. Use `'every'`
     *   to subtract every feature in the layer, or an array (e.g. `[1]`) to
     *   subtract only the listed ids. A bare integer is not accepted — wrap it
     *   in an array — because the integer form produced incorrect results.
     * @param layerName2 Layer name of a second layer - this layer will be also updated if updated:true (specified below)
     * @param id2 ID value for the feature of the Second layer.
     * @param options
     * @param callback - Callback with JSON feature.
     * @throws The 'ids1' parameter must be 'every' or an array of ids.
     * @throws Feature does not have this id property.
     * @throws Could not find feature.
     * @throws No such layer in the map.
     */
    public differenceFeaturesOfLayers (layerName1:string, ids1: 'every' | (string | number)[], layerName2:string, id2:string | number, options?: UpdateOption & GetIdsPropertyOptions & EPSGOptions & IncrementIdOption, callback?: GetDifference) {
        // A bare id (e.g. `1` instead of `[1]`) matches neither branch below, so it
        // would silently subtract nothing and return null. Reject it up front
        // rather than reporting an empty difference as a successful operation.
        if (!Array.isArray(ids1) && !(typeof ids1 === 'string' && ids1.toLowerCase() === 'every')) {
            throw new Error("The 'ids1' parameter must be 'every' or an array of ids.");
        }

        const formatGeoJSON = new GeoJSON();

        const attribute1 = 'attribute1';
        const attribute2 = 'attribute2';

        let idAttribute1 = getIdProperty(options, attribute1);
        let idAttribute2 = getIdProperty(options, attribute2);

        let feature2Options = {
            attribute: idAttribute2,
            format: this.GEOJSON_FORMAT
        }

        let feature2 = this.GIS.Layer.getFeatureByID(layerName2, id2, feature2Options as GetFeatureByIDOptions);

        let difference:TurfFeatureGeneric | null = null;

        if (typeof ids1 !== 'string' && ids1.length > 0) {
            const layer = this.GIS.Layer.getLayer(layerName1);
            const wanted = new Set(ids1.map(id1 => String(id1)));
            const subtrahends: string[] = [];

            layer.getSource().forEachFeature((olFeature: Feature) => {
                if (olFeature.getKeys().filter(key => key === idAttribute1).length === 0) {
                    throw Error('Feature does not have this id property.');
                }

                const id = String(olFeature.get(idAttribute1));
                if (!wanted.has(id)) {
                    return;
                }

                const geometry = olFeature.getGeometry();
                if (!geometry) {
                    return;
                }

                const simpleGeometry = geometry.getType() === 'Circle' ? fromCircle(geometry as Circle) : geometry;
                subtrahends.push(formatGeoJSON.writeGeometry(simpleGeometry));
                wanted.delete(id);
            });

            if (wanted.size > 0) {
                throw new Error('Could not find feature.');
            }

            const differenceResult = this.differenceMany(feature2, subtrahends);
            difference = this.cleanClipResult(differenceResult as AllGeoJSON) as any;
        } else if (typeof ids1 === 'string' && ids1.toLowerCase() === 'every') {
            const layer = this.GIS.Layer.getLayer(layerName1);

            const numberOfFeatures = layer.getSource().getFeatures().length;
            if ((numberOfFeatures < 2 && layerName1 === layerName2) || numberOfFeatures < 1) {
                difference = JSON.parse(feature2);
            } else {
                const subtrahends: string[] = [];

                layer.getSource().forEachFeature((olFeature: Feature) => {
                    const id = olFeature.get(idAttribute1);

                    if (layerName1 === layerName2 && id === id2) {
                        return;
                    }

                    const geometry = olFeature.getGeometry();
                    if (!geometry) {
                        return;
                    }

                    subtrahends.push(formatGeoJSON.writeGeometry(geometry));
                });

                const differenceResult = this.differenceMany(feature2, subtrahends);
                difference = this.cleanClipResult(differenceResult as AllGeoJSON) as any;
            }
        }
        else {
            difference = JSON.parse(feature2);
        }

        if (difference !== null && options && options.update) {

            const feature: Feature<Geometry> = formatGeoJSON.readFeature(difference) as Feature<Geometry>;

            const geometry = feature.getGeometry();

            const featureToUpdate = this.GIS.Layer.getOlFeatureByAttributeID(layerName2, idAttribute2, id2)
            featureToUpdate.setGeometry(geometry);

            // Make sure the updated feature is addressable by an id afterwards
            // (so it can be fetched with getFeatureByID). This happens by default:
            // a feature that already has an id under the id property keeps it
            // (any existing id is left untouched), while one that has none — e.g.
            // an "empty space" keyed only by a real-estate attribute like TUNNUS —
            // gets the next free integer id, numbered the same way a freshly drawn
            // polygon is. The property is 'id' unless a custom one is given via
            // `incrementId`; pass `incrementId: false` to opt out entirely.
            if (options.incrementId !== false && featureToUpdate) {
                const incrementProperty = typeof options.incrementId === 'string' ? options.incrementId : 'id';
                const existingId = featureToUpdate.get(incrementProperty);

                if (existingId === undefined || existingId === null) {
                    const source = this.GIS.Layer.getLayer(layerName2).getSource();
                    featureToUpdate.set(incrementProperty, highestIntegerId(source, incrementProperty) + 1);
                }
            }
        } else if (difference === null && options && options.update) {
            this.GIS.Layer.removeFeatureByID(layerName2, id2, {attribute: idAttribute2})
        }

        if (difference) {
            const attributes = this.GIS.Layer.getFeatureAttributesByID(layerName2, id2, feature2Options);
            delete attributes?.geometry;

            // Refreshed BEFORE the optional reprojection below, while the result is still
            // in the view projection the measurement helper expects. The difference is a
            // smaller shape than the source feature whose attributes it inherits.
            const properties = attributes ? {...attributes} : attributes;
            this.refreshResultMeasurement(properties, difference);

            let feature: Feature<Geometry>;
            if (options && options.epsg) {
                feature = formatGeoJSON.readFeature(difference, {
                    dataProjection: this.GIS.View.olObject.getProjection(),
                    featureProjection: this.GIS.Projection.getRegistered(options.epsg)
                }) as Feature<Geometry>;
            } else {
                feature = formatGeoJSON.readFeature(difference) as Feature<Geometry>;
            }

            difference = JSON.parse(formatGeoJSON.writeFeature(feature));

            if (difference) {
                difference.properties = properties;
            }
        }

        if (callback) {
            difference ? callback(difference) : callback(null);
        }

        return difference;
    }
    /**
     * Break a feature whose geometry is a MultiPolygon into one separate feature
     * per polygon part, so each part becomes individually selectable. An "empty
     * space" built by {@link differenceFeaturesOfLayers} is a single MultiPolygon
     * covering every gap — exploding it lets the user click one gap and get its id.
     *
     * The original feature is removed and replaced in place. Every part keeps a
     * copy of the original attributes and is given a unique id under the id
     * property (default `'id'`, override with `options.attribute`): the first part
     * reuses the original feature's id, each further part gets the next free
     * integer — numbered the same way a freshly drawn polygon is.
     *
     * A feature that is already a single Polygon is left untouched.
     *
     * @param layerName - Layer holding the feature.
     * @param id - Id value of the feature to break apart.
     * @param options - `attribute` is the id property (default 'id').
     * @param callback - Optional callback with the array of resulting ids.
     * @returns Array of the resulting polygon ids (the original id first).
     * @throws No such layer in the map.
     * @throws Could not find feature.
     *
     * @example
     * GIS.Analysis.differenceFeaturesOfLayers('forest_compartment', 'every', 'empty_space', '71068800010028', {
     *     attribute2: 'TUNNUS', update: true
     * });
     * // empty_space now holds one MultiPolygon with id 1 — break it into clickable parts
     * const ids = GIS.Analysis.multiPolygonToPolygons('empty_space', 1);
     */
    public multiPolygonToPolygons(layerName: string, id: string | number, options?: GetIdPropertyOptions, callback?: GetFeaturesId): (string | number)[] {
        const idProperty = getIdProperty(options as GetIdPropertyOptions);

        const olFeature = this.GIS.Layer.getOlFeatureByAttributeID(layerName, idProperty, id);
        if (!olFeature) {
            throw new Error('Could not find feature.');
        }

        const formatGeoJSON = new GeoJSON();
        const geojsonFeature = JSON.parse(formatGeoJSON.writeFeature(olFeature));
        const parts = flatten(geojsonFeature as AllGeoJSON);

        // Already a single polygon — nothing to break apart.
        if (!parts || parts.features.length <= 1) {
            if (callback) {
                callback([id]);
            }
            return [id];
        }

        // The parts are NEW features, so the source's internal style flag must not ride
        // along with the copied attributes.
        const attributes = this.copyableAttributes(olFeature.getProperties());

        this.GIS.Layer.removeFeatureByID(layerName, id, {attribute: idProperty});

        const source = this.GIS.Layer.getLayer(layerName).getSource();
        const ids: (string | number)[] = [];

        parts.features.forEach((part, index) => {
            const partFeature = formatGeoJSON.readFeature(part) as Feature<Geometry>;

            // The first part keeps the original id, the rest get the next free integer.
            const partId = index === 0 ? id : highestIntegerId(source, idProperty) + 1;

            partFeature.setProperties({...attributes});
            partFeature.set(idProperty, partId);

            // Each part inherits the original feature's attributes, which include the
            // whole-MultiPolygon area / border-length values. Recompute each part from
            // its own geometry so a measured feature's overlay shows the part's own size
            // rather than the pre-split whole. Only measured features are touched, and
            // this also wires the part for automatic recalculation on later edits.
            this.GIS.Edit.recalculateMeasurement(partFeature);

            source.addFeature(partFeature);
            ids.push(partId);
        });

        if (callback) {
            callback(ids);
        }

        return ids;
    }
    /**
     * Find all pairs of mutually overlapping polygon features within one layer.
     *
     * Optimized for layers with many polygons:
     * - geometries are serialized to GeoJSON only once,
     * - candidate pairs are pre-filtered through the layer's R-tree spatial index (`getFeaturesInExtent`),
     *   so only features with intersecting bounding boxes are intersected,
     * - every pair is checked exactly once.
     *
     * Sliver overlaps are not reported: two polygons whose boundaries merely graze
     * each other intersect in a hair-thin strip that is a clipper artifact, not an
     * overlap the user has to resolve. Both the area and the mean-width test of
     * {@link SliverThresholds} are applied — see `minArea` / `maxWidth`.
     *
     * @param layerName - The layer to check for overlaps.
     * @param options - `attribute` is the id property (default 'id'), `minArea` the sliver area
     * threshold in m² (default 0.05), `maxWidth` the sliver mean-width threshold in m (default 0.1),
     * `targetLayer` an optional layer to fill with the overlap geometries (cleared first).
     * @param callback - Optional callback with the array of overlapping pairs.
     * @returns Array of `{ids: [id1, id2], feature}` where `feature` is the overlap as a GeoJSON Feature.
     * @throws No such layer in the map.
     *
     * @example
     * // Show all overlaps of forest compartments in the "overlapping_space" layer
     * const pairs = GIS.Analysis.getOverlappingFeaturePairs('forest_compartment', {
     *     attribute: 'id',
     *     targetLayer: 'overlapping_space'
     * });
     */
    public getOverlappingFeaturePairs (layerName: string, options?: GetOverlapsOption & GetIdPropertyOptions, callback?: GetOverlappingPairs): OverlappingPair[] {
        const idAttribute = getIdProperty(options);
        const minArea = options && options.minArea !== undefined ? options.minArea : DEFAULT_SLIVER_MIN_AREA;
        const maxWidth = options && options.maxWidth !== undefined ? options.maxWidth : DEFAULT_SLIVER_MAX_WIDTH;

        const source = this.GIS.Layer.getLayer(layerName).getSource();
        const features: Feature[] = source.getFeatures();

        // Serialize every polygon geometry to a Turf Feature exactly once.
        type OverlapEntry = {
            index: number,
            id: string | number,
            geometry: Geometry,
            turfFeature: TurfFeatureGeneric
        };

        const indexByFeature = new Map<Feature, number>();
        const entries: (OverlapEntry | null)[] = features.map((olFeature, index) => {
            indexByFeature.set(olFeature, index);

            const geometry = olFeature.getGeometry();
            if (!geometry) {
                return null;
            }

            const geometryType = geometry.getType();
            if (geometryType !== 'Polygon' && geometryType !== 'MultiPolygon' && geometryType !== 'Circle') {
                return null;
            }

            const simpleGeometry = geometryType === 'Circle' ? fromCircle(geometry as Circle) : geometry;

            return {
                index,
                id: olFeature.get(idAttribute),
                geometry: simpleGeometry,
                turfFeature: this.toTurfFeature(this.geoJSONFormat.writeGeometry(simpleGeometry)) as TurfFeatureGeneric
            };
        });

        const pairs: OverlappingPair[] = [];

        entries.forEach(entry => {
            if (!entry) {
                return;
            }

            // R-tree lookup: only features whose bounding boxes intersect are candidates.
            const candidates: Feature[] = source.getFeaturesInExtent(entry.geometry.getExtent());

            candidates.forEach(candidate => {
                const candidateIndex = indexByFeature.get(candidate);

                // Process each pair only once (i < j).
                if (candidateIndex === undefined || candidateIndex <= entry.index) {
                    return;
                }

                const other = entries[candidateIndex];
                if (!other) {
                    return;
                }

                const rawIntersect = intersect(featureCollection([entry.turfFeature, other.turfFeature]) as FeatureCollection<Polygon | MultiPolygon>);
                if (!rawIntersect) {
                    return;
                }

                const overlap = this.cleanClipResult(rawIntersect as AllGeoJSON, {minArea, maxWidth});
                if (!overlap) {
                    return;
                }

                overlap.properties = {
                    [idAttribute]: entry.id + '-' + other.id,
                    id1: entry.id,
                    id2: other.id
                };

                pairs.push({
                    ids: [entry.id, other.id],
                    feature: overlap
                });
            });
        });

        if (options && options.targetLayer) {
            const targetLayer = options.targetLayer;
            const epsgCode = this.GIS.View.olObject.getProjection().getCode().replace('EPSG:', '');

            this.GIS.Layer.removeFeatures(targetLayer);
            pairs.forEach(pair => {
                this.GIS.Layer.addFeatures(targetLayer, pair.feature, {epsg: epsgCode});
            });
        }

        if (callback) {
            callback(pairs);
        }

        return pairs;
    }

    /**
     * Intersect two features specified by ID from two or similar layer
     * @param layerName1 Layer name of a first layer
     * @param id1 ID value for the feature of the First layer.
     * @param layerName2 Layer name of a second layer - this layer will be also updated if updated:true (specified below)
     * @param id2 ID value for the feature of the Second layer.
     * @param options
     * @param callback - Callback with JSON feature.
     * @throws Feature does not have this id property.
     * @throws Could not find feature.
     * @throws No such layer in the map.
     */
    public intersectFeaturesOfLayers (layerName1: string, id1: string | number, layerName2: string, id2: string | number, options?: GetIdsPropertyOptions & UpdateOption & EPSGOptions, callback?: GetIntersect) {
        const formatGeoJSON = new GeoJSON();

        const attribute1 = 'attribute1';
        const attribute2 = 'attribute2';

        let idAttribute1 = getIdProperty(options, attribute1);
        let idAttribute2 = getIdProperty(options, attribute2);

        let feature1Options = {
            attribute: idAttribute1,
            format: this.GEOJSON_FORMAT
        }

        let feature2Options = {
            attribute: idAttribute2,
            format: this.GEOJSON_FORMAT
        }

        const feature1 = this.GIS.Layer.getFeatureByID(layerName1, id1, feature1Options as GetFeatureByIDOptions);
        const feature2 = this.GIS.Layer.getFeatureByID(layerName2, id2, feature2Options as GetFeatureByIDOptions);

        const rawIntersect = this.intersectFeatures(feature1, feature2);
        let intersect = this.cleanClipResult(rawIntersect as AllGeoJSON);

        if (intersect !== null && options && options.update) {
            const format = new GeoJSON();
            const feature = format.readFeature(intersect, {
                dataProjection: this.GIS.View.olObject.getProjection(),
                featureProjection: this.GIS.View.olObject.getProjection()
            }) as Feature<Geometry>;

            const geometry = feature.getGeometry();

            const featureToUpdate = this.GIS.Layer.getOlFeatureByAttributeID(layerName2, idAttribute2, id2)
            featureToUpdate.setGeometry(geometry);
        }

        if (intersect && intersect.properties) {
            const attributes = this.GIS.Layer.getFeatureAttributesByID(layerName2, id2, feature2Options);
            delete attributes?.geometry;
            intersect.properties = {...attributes};

            // Refresh while the result is still in the view projection — the measurement
            // is taken from the intersection's own geometry, not the source polygon's.
            this.refreshResultMeasurement(intersect.properties, intersect);

            if (options && options.epsg) {
                const feature: Feature<Geometry> = formatGeoJSON.readFeature(intersect, {
                    dataProjection: this.GIS.View.olObject.getProjection(),
                    featureProjection: this.GIS.Projection.getRegistered(options.epsg)
                }) as Feature<Geometry>;

                intersect = JSON.parse(formatGeoJSON.writeFeature(feature));
            }
        }

        if (callback) {
            intersect ? callback(intersect) : callback(null);
        }

        return intersect;
    }
    /**
     * Check all features of Layer1 if spatially contained in Layer2
     * @param layerName1 Layer1 - all features checked
     * @param layerName2 - Layer2 - one feature is checked if contain features
     * @param id ID value for the feature of Layer2.
     * @param options
     * @param callback - Callback with attributes in JSON format.
     * @throws Feature does not have this id property.
     * @throws Could not find feature.
     * @throws No such layer in the map.
     */
    public getFeaturesAttributesContainedInFeature (layerName1: string, layerName2: string, id: string | number, options?: GetIdPropertyOptions, callback?: GetFeaturesAttributesContainedInFeature) {
        const attribute: {[p: string]: any}[] = [];
        let idProperty = getIdProperty(options);

        let getFeatureOptions = {
            attribute: idProperty,
            format: this.GEOJSON_FORMAT
        }

        const layer = this.GIS.Layer.getLayer(layerName1);
        const envelopeFeature = this.GIS.Layer.getFeatureByID(layerName2, id, getFeatureOptions as GetFeatureByIDOptions);

        layer.getSource().forEachFeature((feature:Feature<Geometry>) => {
            const geometry = feature.getGeometry();
            const format = new GeoJSON();
            if (geometry) {
                const geometryRepresentation = format.writeGeometry(geometry, {
                    dataProjection: this.GIS.View.olObject.getProjection(),
                    featureProjection: this.GIS.View.olObject.getProjection()
                });

                const contains = this.booleanContainsFeatures(envelopeFeature, geometryRepresentation)
                if (contains) {
                    const featureAttributes = feature.getProperties();
                    attribute.push(featureAttributes);
                }
            }
        });

        if (callback) {
            callback(attribute);
        }

        return attribute;
    };
    /**
     * Check whether a coordinate falls inside any polygon feature of a layer.
     *
     * This answers "am I in or out of this boundary?" for a single point — the
     * core primitive behind boundary-aware coordinate labels (e.g. the Swedish
     * territory boundary used by MDP to switch coordinate tooltip formats).
     *
     * The test is a true geometric point-in-polygon check (OpenLayers
     * `intersectsCoordinate`), so it does **not** depend on the boundary layer
     * being visible or rendered. Only `Polygon` and `MultiPolygon` geometries
     * are considered; any other geometry type in the layer is ignored.
     *
     * @param coordinate - Coordinate to test. In the map view projection unless
     *   `options.epsg` is given, in which case it is transformed from that
     *   projection to the view projection before testing.
     * @param layerName - Name of the boundary layer.
     * @param options - Optional `epsg` of the incoming coordinate.
     * @param callback - Optional callback receiving the boolean result.
     * @returns `true` if the coordinate is inside any polygon of the layer.
     * @throws No such layer in the map.
     *
     * @example
     * // Is the click inside the Swedish boundary layer?
     * const inside = GIS.Analysis.isCoordinateInsideLayer([x, y], 'sweden_boundary');
     */
    public isCoordinateInsideLayer(coordinate: Coordinate, layerName: string, options?: EPSGOptions, callback?: (inside: boolean) => void): boolean {
        const source = this.GIS.Layer.getLayer(layerName).getSource();

        let testCoordinate = coordinate;
        if (options && options.epsg) {
            testCoordinate = transform(
                coordinate,
                this.GIS.Projection.getRegistered(options.epsg),
                this.GIS.View.olObject.getProjection().getCode()
            );
        }

        const match = source.forEachFeature((feature: Feature<Geometry>) => {
            const geometry = feature.getGeometry();
            if (!geometry) {
                return undefined;
            }

            const type = geometry.getType();
            if (type !== 'Polygon' && type !== 'MultiPolygon') {
                return undefined;
            }

            return geometry.intersectsCoordinate(testCoordinate) ? feature : undefined;
        });

        const inside = !!match;

        if (callback) {
            callback(inside);
        }

        return inside;
    }
    /**
     * Creates an offset polygon along a line feature. For a "full" offset the function generates parallel lines
     * on both sides and constructs a polygon between them. For "left" or "right" options, a polygon is created between
     * the original line and its single offset.
     *
     * ⚠️ This method assumes that the geometry is in a projected coordinate system (e.g., EPSG:3857).
     * It is **not suitable for EPSG:4326** or long distances over spherical geometries, as it doesn't take the Earth's curvature into account.
     * If an `epsg` option is passed, the geometry will be transformed to that coordinate system before offsetting.
     *
     * If `format: 'geojson'` is passed (or not specified), the result is returned as a GeoJSON Polygon Feature.
     * If `format: 'simple'` is passed, the result is returned as a simple OpenLayers geometry.
     *
     * @param layerName - The layer name containing the line feature.
     * @param id - The ID of the line feature to offset.
     * @param distance - Offset distance.
     * @param side - "full", "left", or "right".
     * @param options - Buffer options (units, epsg, format).
     * @param callback - Optional callback to receive the resulting polygon or geometry.
     * @returns The resulting offset geometry as either a GeoJSON Feature or a simple geometry, or null if the input is not found.
     */
    public createLineOffset(
        layerName: string,
        id: string | number,
        distance: number,
        side: "left" | "right" | "full",
        options?: BufferOptions & EPSGOptions & CreateLineOffsetExportFormat,
        callback?: (result: any) => void
    ) {
        const formatGeoJSON = new GeoJSON();
        const originalProjection = this.GIS.View.olObject.getProjection();
        const targetProjection = options && options.epsg ? this.GIS.Projection.getRegistered(options.epsg) : originalProjection;
        const unitFactor = options && options.units === 'kilometers' ? 1000 : 1;

        const feature = this.GIS.Layer.getFeatureByID(layerName, id, {
            attribute: 'id',
            format: this.GEOJSON_FORMAT
        });

        if (!feature) {
            if (callback) callback(null);
            return null;
        }

        const featureProj = formatGeoJSON.readFeature(feature, {
            dataProjection: originalProjection,
            featureProjection: targetProjection
        });

        const featureGeoJSONProj = JSON.parse(formatGeoJSON.writeFeature(featureProj as Feature<Geometry>));

        const adjustedDistance = distance * unitFactor;

        let result;

        if (side === "full") {
            const leftOffsetLine = getLineOffsetMeters(featureGeoJSONProj, -adjustedDistance/2);
            const rightOffsetLine = getLineOffsetMeters(featureGeoJSONProj, adjustedDistance/2);

            const leftCoords = leftOffsetLine.geometry.coordinates;
            const rightCoords = rightOffsetLine.geometry.coordinates.reverse();

            const polyCoords = [leftCoords.concat(rightCoords, [leftCoords[0]])];
            result = polygon(polyCoords);
        } else {
            const offsetDistance = side === "left" ? adjustedDistance : -adjustedDistance;
            const offsetLine = getLineOffsetMeters(featureGeoJSONProj, offsetDistance);

            const offsetCoords = offsetLine.geometry.coordinates.reverse();
            const polyCoords = [featureGeoJSONProj.geometry.coordinates.concat(offsetCoords, [featureGeoJSONProj.geometry.coordinates[0]])];

            result = polygon(polyCoords);
        }

        if (options && options.format === 'simple') {
            const simpleGeometry = formatGeoJSON.readGeometry(result.geometry, {
                dataProjection: targetProjection,
                featureProjection: originalProjection
            });

            if (callback) callback(simpleGeometry);
            return simpleGeometry;
        }

        if (callback) {
            callback(result);
        }

        return result;
    }
}

export default Analysis;
